import { useState, FormEvent } from 'react';
import { TimePicker } from '../ui/TimePicker';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface HealthMetricFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

type MetricSubTab = 'medication' | 'vitamin' | 'bowel' | 'symptom';

const metricSubTabs: { id: MetricSubTab; label: string }[] = [
	{ id: 'medication', label: 'Obat' },
	{ id: 'vitamin', label: 'Vitamin' },
	{ id: 'bowel', label: 'BAB' },
	{ id: 'symptom', label: 'Gejala' },
];

/**
 * HealthMetricForm Component
 *
 * Form for logging health metrics (medications, vitamins, bowel movements, symptoms).
 * Posts to /api/health-metrics endpoint.
 */
export function HealthMetricForm({
	selectedDate,
	onSuccess,
}: HealthMetricFormProps) {
	const [activeSubTab, setActiveSubTab] = useState<MetricSubTab>('medication');
	const [description, setDescription] = useState('');
	const [hasBowelMovement, setHasBowelMovement] = useState(true);
	const [time, setTime] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingBlobs, setPendingBlobs] = useState<Blob[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0);

	const resetForm = () => {
		setDescription('');
		setHasBowelMovement(true);
		setTime('');
		setNotes('');
		setPendingBlobs([]);
		setUploaderKey((k) => k + 1);
	};

	const isBowelTab = activeSubTab === 'bowel';

	const uploadImages = async (entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			const result = await convertToAVIF(blob);

			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', 'health');
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());

			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate description for non-bowel types
		if (!isBowelTab && !description.trim()) {
			const label = metricSubTabs.find((t) => t.id === activeSubTab)?.label;
			setError(`${label} harus diisi`);
			setIsSubmitting(false);
			return;
		}

		try {
			const body: Record<string, unknown> = {
				date: selectedDate,
				metricType: activeSubTab,
			};

			if (isBowelTab) {
				body.hasBowelMovement = hasBowelMovement ? 'yes' : 'no';
				if (time) body.time = `${selectedDate}T${time}:00`;
			} else {
				body.description = description.trim();
			}

			if (notes) body.notes = notes;

			const res = await fetch(apiUrl('/api/health-metrics'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify(body),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (pendingBlobs.length > 0) {
				await uploadImages(data.id, pendingBlobs);
			}

			const tabLabel = metricSubTabs.find((t) => t.id === activeSubTab)?.label;
			onSuccess?.(`${tabLabel} berhasil dicatat!`);
			resetForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Kesehatan</h2>

			{/* Sub-tabs for metric type */}
			<div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
				{metricSubTabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => {
							setActiveSubTab(tab.id);
							resetForm(); // Reset form when switching tabs
						}}
						className={`px-4 py-2.5 min-h-11 text-sm font-medium rounded-full transition ${
							activeSubTab === tab.id
								? 'bg-blue-100 text-blue-700'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			{/* Bowel Movement Form */}
			{isBowelTab && (
				<>
					{/* Yes/No Toggle */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Apakah BAB hari ini?
						</label>
						<div className="flex gap-3">
							<button
								type="button"
								onClick={() => setHasBowelMovement(true)}
								className={`flex-1 py-3 px-4 min-h-12 rounded-lg font-medium transition ${
									hasBowelMovement
										? 'bg-green-100 text-green-700 border-2 border-green-500'
										: 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
								}`}
							>
								Ya
							</button>
							<button
								type="button"
								onClick={() => setHasBowelMovement(false)}
								className={`flex-1 py-3 px-4 min-h-12 rounded-lg font-medium transition ${
									!hasBowelMovement
										? 'bg-red-100 text-red-700 border-2 border-red-500'
										: 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
								}`}
							>
								Tidak
							</button>
						</div>
					</div>

					{/* Time Picker (only if bowel movement occurred) */}
					{hasBowelMovement && (
						<TimePicker
							value={time}
							onChange={setTime}
							label="Waktu (opsional)"
							id="bowel-time"
							disabled={isSubmitting}
						/>
					)}
				</>
			)}

			{/* Description Form (for medication, vitamin, symptom) */}
			{!isBowelTab && (
				<div>
					<label
						htmlFor="metric-description"
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						{activeSubTab === 'medication' && (
							<>
								Nama Obat <span className="text-red-500">*</span>
							</>
						)}
						{activeSubTab === 'vitamin' && (
							<>
								Nama Vitamin <span className="text-red-500">*</span>
							</>
						)}
						{activeSubTab === 'symptom' && (
							<>
								Deskripsi Gejala <span className="text-red-500">*</span>
							</>
						)}
					</label>
					<textarea
						id="metric-description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						rows={3}
						placeholder={
							activeSubTab === 'medication'
								? 'Contoh: Metformin 500mg, setelah makan'
								: activeSubTab === 'vitamin'
									? 'Contoh: Vitamin D3 1000 IU'
									: 'Contoh: Pusing ringan, mual'
						}
						required
						disabled={isSubmitting}
						className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
					/>
				</div>
			)}

			{/* Notes Input */}
			<div>
				<label
					htmlFor="metric-notes"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Catatan (opsional)
				</label>
				<textarea
					id="metric-notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={2}
					placeholder="Catatan tambahan..."
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Image Upload Section (inline) */}
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Foto (opsional)
				</label>
				<ImageUploader
					key={uploaderKey}
					entryType="health"
					onPendingBlobsChange={setPendingBlobs}
					disabled={isSubmitting}
				/>
			</div>

			{/* Submit Button - Sticky on mobile for easy access */}
			<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
				<button
					type="submit"
					disabled={isSubmitting}
					className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
				>
					{isSubmitting ? 'Menyimpan...' : 'Simpan'}
				</button>
			</div>
		</form>
	);
}
